/**
 * CustomerProposalsTab - Aba "Propostas" dos modais de Cliente e Contato
 *
 * Lista TODAS as propostas dos negócios do cliente (ou do contato, no modo
 * Contato) através da RPC get_customer_proposals, que já aplica as regras de
 * visibilidade de negócios (can_user_view_crm_deal).
 *
 * Reaproveita integralmente a linha da aba Propostas do modal de negócio
 * (ProposalListRow) e os mesmos modais de detalhes, PDF, compartilhamento e
 * confirmação com motivo — mantendo a experiência idêntica nas duas telas.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.1.0 - Visualização agrupada por negócio (padrão) + alternância Lista
 *   RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */

import React, { useEffect, useMemo, useState } from 'react';
import { FileText, LayoutList, List } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CustomerProposalGroup } from './proposals/CustomerProposalGroup';
import {
  buildPublicProposalUrl,
  useCustomerProposals,
  useDealProposals,
  useProposalPdf,
  useProposalShare,
  type DealProposal,
} from '@/hooks/useDealProposals';
import { ProposalListRow } from '@/components/crm/deals/modal/tabs/proposals/ProposalListRow';
import { ProposalDetailsModal } from '@/components/crm/deals/modal/tabs/proposals/ProposalDetailsModal';
import { ProposalPdfPreviewModal } from '@/components/crm/deals/modal/tabs/proposals/ProposalPdfPreviewModal';
import { ProposalStatusReasonDialog } from '@/components/crm/deals/modal/tabs/proposals/ProposalStatusReasonDialog';
import { ProposalShareLauncher } from '@/components/crm/deals/modal/tabs/proposals/ProposalShareLauncher';

/** Chave de persistência do modo de visualização da aba */
const VIEW_MODE_STORAGE_KEY = 'customer:proposals:view-mode';

interface CustomerProposalsTabProps {
  /** Cliente (modo Cliente) */
  customerId: string | null;
  /** Contato (modo Contato ou contato principal) */
  contactId: string | null;
  /** Aba visível — controla o lazy loading da consulta */
  isActive: boolean;
  /** Permissão de edição (transições de situação) */
  canEdit?: boolean;
}

export function CustomerProposalsTab({
  customerId,
  contactId,
  isActive,
  canEdit = true,
}: CustomerProposalsTabProps) {
  const { proposals, isLoading } = useCustomerProposals(customerId, contactId, isActive);
  // Mutations compartilhadas (updateStatus não depende do negócio)
  const { updateStatus } = useDealProposals(null, false);
  const { generatePdf, openPdf } = useProposalPdf();
  const { sendProposal } = useProposalShare();

  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [shareProposalId, setShareProposalId] = useState<string | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; title: string; path: string | null } | null>(
    null,
  );
  const [reasonAction, setReasonAction] = useState<{
    proposal: DealProposal;
    status: 'rejected' | 'cancelled';
  } | null>(null);

  // Modo de visualização (agrupada por negócio é o padrão), persistido no navegador
  const [viewMode, setViewMode] = useState<'grouped' | 'list'>(() => {
    try {
      const raw = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      if (raw === 'grouped' || raw === 'list') return raw;
    } catch {
      /* ignora SSR/quota */
    }
    return 'grouped';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* ignora SSR/quota */
    }
  }, [viewMode]);

  // Agrupamento em memória por negócio, preservando a ordem da consulta
  const groups = useMemo(() => {
    const map = new Map<string, { dealId: string; dealTitle: string | null; proposals: typeof proposals }>();
    proposals.forEach((p) => {
      const key = p.deal_id;
      const existing = map.get(key);
      if (existing) existing.proposals.push(p);
      else map.set(key, { dealId: key, dealTitle: p.deal_title, proposals: [p] });
    });
    return Array.from(map.values());
  }, [proposals]);

  /** Gera o link público (quando ainda não existe) e copia para a área de transferência */
  const handleGenerateLink = async (proposal: DealProposal) => {
    const result = await sendProposal.mutateAsync({ proposalId: proposal.id });
    try {
      await navigator.clipboard.writeText(result.url);
      toast.success('Link público gerado e copiado');
    } catch {
      toast.success('Link público gerado');
    }
  };

  /** Copia o link público já existente */
  const handleCopyLink = async (proposal: DealProposal) => {
    if (!proposal.public_token) return;
    try {
      await navigator.clipboard.writeText(buildPublicProposalUrl(proposal.public_token));
      toast.success('Link copiado');
    } catch {
      toast.error('Copie o link manualmente pelo modal de detalhes');
    }
  };

  /** Transições de situação — recusar/cancelar exigem motivo */
  const handleStatus = async (
    proposal: DealProposal,
    status: 'sent' | 'accepted' | 'rejected' | 'cancelled',
  ) => {
    if (status === 'rejected' || status === 'cancelled') {
      setReasonAction({ proposal, status });
      return;
    }
    await updateStatus.mutateAsync({ proposalId: proposal.id, status });
  };

  /** Confirma a transição destrutiva com o motivo informado */
  const handleConfirmReason = (reason: string) => {
    if (!reasonAction) return;
    updateStatus.mutate(
      { proposalId: reasonAction.proposal.id, status: reasonAction.status, reason },
      { onSuccess: () => setReasonAction(null) },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  // Props comuns da linha (idênticas nos dois modos de visualização)
  const rowProps = {
    canAct: canEdit,
    onOpenDetails: setDetailsId,
    onShare: setShareProposalId,
    onCopyLink: handleCopyLink,
    onGenerateLink: handleGenerateLink,
    isGeneratingLink: sendProposal.isPending,
    onStatus: handleStatus,
    onPreviewPdf: (proposal: DealProposal, path: string | null) =>
      setPreview({
        id: proposal.id,
        title: proposal.title || proposal.proposal_number,
        path,
      }),
    onDownloadPdf: openPdf,
    onGeneratePdf: async (proposal: DealProposal) => {
      setPdfBusyId(proposal.id);
      try {
        const result = await generatePdf.mutateAsync(proposal.id);
        if (result?.storage_path) {
          setPreview({
            id: proposal.id,
            title: proposal.title || proposal.proposal_number,
            path: result.storage_path,
          });
        }
      } finally {
        setPdfBusyId(null);
      }
    },
    pdfBusyId,
    isPdfPending: generatePdf.isPending,
  };

  return (
    <div className="space-y-3">
      {/* Resumo + alternância de visualização */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {proposals.length} proposta{proposals.length === 1 ? '' : 's'}
          {viewMode === 'grouped' && groups.length > 0
            ? ` · ${groups.length} negócio${groups.length === 1 ? '' : 's'}`
            : ''}
        </p>

        {proposals.length > 0 && (
          <div className="flex items-center gap-1 rounded-md border p-0.5">
            <Button
              type="button"
              size="sm"
              variant={viewMode === 'grouped' ? 'secondary' : 'ghost'}
              className="h-6 px-2 text-[11px] gap-1"
              onClick={() => setViewMode('grouped')}
            >
              <LayoutList className="h-3 w-3" />
              Agrupada
            </Button>
            <Button
              type="button"
              size="sm"
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              className="h-6 px-2 text-[11px] gap-1"
              onClick={() => setViewMode('list')}
            >
              <List className="h-3 w-3" />
              Lista
            </Button>
          </div>
        )}
      </div>

      {proposals.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center gap-2 text-center">
          <FileText className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">Nenhuma proposta encontrada</p>
          <p className="text-xs text-muted-foreground">
            As propostas são geradas dentro de cada negócio, na aba Propostas.
          </p>
        </div>
      ) : viewMode === 'grouped' ? (
        // Visualização agrupada por negócio (padrão)
        <div className="space-y-2">
          {groups.map((group, index) => (
            <CustomerProposalGroup
              key={group.dealId}
              dealId={group.dealId}
              dealTitle={group.dealTitle}
              proposals={group.proposals}
              defaultExpanded={index === 0}
              {...rowProps}
            />
          ))}
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {proposals.map((p) => (
            <ProposalListRow key={p.id} proposal={p} dealLabel={p.deal_title} {...rowProps} />
          ))}

        </div>
      )}

      {/* Modal de detalhes (snapshot + histórico) */}
      <ProposalDetailsModal proposalId={detailsId} onClose={() => setDetailsId(null)} />

      {/* Compartilhamento direto pela listagem */}
      <ProposalShareLauncher
        proposalId={shareProposalId}
        onClose={() => setShareProposalId(null)}
      />

      {/* Preview interno do PDF */}
      <ProposalPdfPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        proposalId={preview?.id ?? null}
        title={preview?.title}
        storagePath={preview?.path ?? null}
      />

      {/* Confirmação com motivo para recusar/cancelar */}
      <ProposalStatusReasonDialog
        open={!!reasonAction}
        onOpenChange={(open) => !open && setReasonAction(null)}
        onConfirm={handleConfirmReason}
        isPending={updateStatus.isPending}
        title={
          reasonAction?.status === 'rejected'
            ? 'Marcar proposta como recusada'
            : 'Cancelar proposta'
        }
        description={
          reasonAction?.status === 'rejected'
            ? 'A proposta passará para a situação Recusada e deixará de valer como oferta em aberto.'
            : 'A proposta será cancelada e deixará de valer como documento comercial ativo.'
        }
        noticeText="O link público é desativado e a proposta não poderá mais ser aceita pelo cliente."
        reasonLabel={
          reasonAction?.status === 'rejected' ? 'Motivo da recusa' : 'Motivo do cancelamento'
        }
        confirmLabel={
          reasonAction?.status === 'rejected' ? 'Confirmar recusa' : 'Confirmar cancelamento'
        }
      />
    </div>
  );
}
